import { useState } from 'react';
import type { MessageKey } from '@bookingdei/i18n/message-key';
import { formatPhone } from '@bookingdei/i18n/phone';
import { phonePlan } from '../theme/phone-plan';
import { useLanguage } from './language-context';
import { useSettings } from './settings-context';
import { Sheet } from './sheet';
import { NavIcon } from './nav-icon';
import type { NavIconName } from './nav-icon';

// The rail footer's Help panel and Contact sheet (design M:4840-4900 +
// helpFaqs / contactRows M:12318-12335). Not routes (PRD §7.3.3 top-bar row).
//
// Help: a four-question accordion. The CMS has no merchant FAQ (GET /pages/{slug}
// serves one markdown body for the website `help` page), so the questions are
// bundled i18n strings (DR-16) describing the launch dashboard — not the design's
// SMS-credit answer, which is deferred (DR-32).
//
// Contact: WhatsApp / email / hotline rows built from the registry's platform
// contact channels (GET /registries/settings `contact` + `support_hours`, API-210) —
// never a literal in the surface (INF-1). A channel the registry omits is not shown.
const FAQ: readonly { q: MessageKey; a: MessageKey }[] = [
  { q: 'dc70.help.q1', a: 'dc70.help.a1' },
  { q: 'dc70.help.q2', a: 'dc70.help.a2' },
  { q: 'dc70.help.q3', a: 'dc70.help.a3' },
  { q: 'dc70.help.q4', a: 'dc70.help.a4' },
];

export function HelpPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useLanguage();
  const [openIdx, setOpenIdx] = useState(-1);
  return (
    <Sheet open={open} onClose={onClose} title={t('dc70.help.title')} width={480}>
      <div className="help-sheet" data-shell="help-panel">
        <p className="help-sheet__sub">{t('dc70.help.sub')}</p>
        <div className="help-faq">
          {FAQ.map((f, i) => {
            const expanded = openIdx === i;
            const panelId = `help-faq-${i}`;
            return (
              <div key={f.q} className="help-faq__item">
                <button
                  type="button"
                  className="help-faq__q"
                  aria-expanded={expanded}
                  aria-controls={panelId}
                  onClick={() => setOpenIdx(expanded ? -1 : i)}
                >
                  <span>{t(f.q)}</span>
                  <span className={`help-faq__chev${expanded ? ' is-open' : ''}`}>
                    <NavIcon name="chevron" size={16} strokeWidth={2} />
                  </span>
                </button>
                {expanded && (
                  <p id={panelId} className="help-faq__a">
                    {t(f.a)}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Sheet>
  );
}

// Registry-driven contact rows; exported for its test.
export function contactRows(
  contact: { whatsapp?: string; email?: string; hotline?: string } | undefined,
): { key: MessageKey; sub: string; href: string; icon: NavIconName }[] {
  if (!contact) return [];
  const digits = (p: string) => p.replace(/[^\d]/g, '');
  const rows: { key: MessageKey; sub: string; href: string; icon: NavIconName }[] = [];
  if (contact.whatsapp)
    rows.push({
      key: 'dc70.contact.whatsapp',
      sub: formatPhone(contact.whatsapp, phonePlan),
      href: `https://wa.me/${digits(contact.whatsapp)}`,
      icon: 'chat',
    });
  if (contact.email)
    rows.push({
      key: 'dc70.contact.email',
      sub: contact.email,
      href: `mailto:${contact.email}`,
      icon: 'inbox',
    });
  if (contact.hotline)
    rows.push({
      key: 'dc70.contact.call',
      sub: formatPhone(contact.hotline, phonePlan),
      href: `tel:${contact.hotline.replace(/\s/g, '')}`,
      icon: 'phone',
    });
  return rows;
}

export function ContactSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { lang, t } = useLanguage();
  const { settings } = useSettings();
  const rows = contactRows(settings?.contact);
  const hoursRaw = (settings?.support_hours ?? {}) as Record<string, unknown>;
  const hours = typeof hoursRaw[lang] === 'string' ? (hoursRaw[lang] as string) : '';
  return (
    <Sheet open={open} onClose={onClose} title={t('dc70.contact.title')} width={400}>
      <div className="help-sheet" data-shell="contact-sheet">
        {hours && <p className="help-sheet__sub">{hours}</p>}
        {rows.length === 0 ? (
          <p className="help-sheet__sub">{t('dc70.contact.none')}</p>
        ) : (
          <div className="contact-rows">
            {rows.map((r) => (
              <a key={r.key} className="contact-row" href={r.href} target="_blank" rel="noreferrer">
                <span className="contact-row__icon">
                  <NavIcon name={r.icon} size={16} strokeWidth={1.9} />
                </span>
                <span className="contact-row__text">
                  <span className="contact-row__label">{t(r.key)}</span>
                  <span className="contact-row__sub">{r.sub}</span>
                </span>
                <NavIcon name="external" size={15} strokeWidth={1.75} className="contact-row__go" />
              </a>
            ))}
          </div>
        )}
      </div>
    </Sheet>
  );
}
